/**
 * Get an image out of a drop, when there is one to get.
 *
 * The bug this exists to fix: a drag off the Finder always carries a real
 * `File` in `dataTransfer.files`, and a drag of an `<img>` off a web page
 * often does not. Chrome usually downloads the image itself and synthesizes
 * a `File` for you; Safari more often does not, and hands over only the URL
 * the image was rendered from — in `text/uri-list`, or buried in the
 * `text/html` the drag also carries. A drop handler that reads only
 * `dataTransfer.files` sees nothing in that case and does nothing, silently
 * — which is exactly "I tried to drag but the image didn't actually
 * replace" with no error to explain it.
 *
 * So this reads the File directly where the browser already produced one,
 * and failing that, pulls a URL out of whichever carrier has it and fetches
 * the bytes itself. A fetch that fails — a cross-origin refusal, a dead
 * link — is reported rather than retried a different way: routing around a
 * refusal is exactly what CLAUDE.md's rule 4 exists to stop, even for a page
 * this app did not choose to fetch on its own.
 *
 * No imports, so `npm test` can load it, and safe to import from `admin/`
 * as well as `app/` for the same reason `sourcing.ts` is: nothing here
 * touches React.
 */

export type DropImageResult =
  | { file: File }
  | { error: string };

function firstUri(list: string): string | null {
  const line = list.split(/\r\n|\r|\n/).find((l) => l && !l.startsWith('#'));
  return line || null;
}

function imgSrcFrom(html: string): string | null {
  const m = /<img[^>]+src=["']([^"']+)["']/i.exec(html);
  return m ? m[1] : null;
}

/** The URL a drop's `text/uri-list` or `text/html` carrier points at, or
 *  null when neither carries one. Exported on its own because it is the one
 *  part of this worth testing without a real `DataTransfer` or a network. */
export function imageUrlFromDrop(dt: { getData(format: string): string }): string | null {
  return firstUri(dt.getData('text/uri-list')) ?? imgSrcFrom(dt.getData('text/html'));
}

export async function imageFromDrop(dt: DataTransfer): Promise<DropImageResult> {
  const direct = [...dt.files].find((f) => f.type.startsWith('image/'));
  if (direct) return { file: direct };

  const url = imageUrlFromDrop(dt);
  if (!url) {
    return { error: 'That did not carry an image. Try dragging from Finder, or use "Choose a file".' };
  }

  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(String(res.status));
    const blob = await res.blob();
    if (!blob.type.startsWith('image/')) {
      return { error: 'That link did not point at an image.' };
    }
    return { file: new File([blob], 'dropped-image', { type: blob.type }) };
  } catch {
    // Almost always a cross-origin refusal — the site did not opt the image
    // in to being read from another origin, which curl would see as the
    // same 403 a recipe fetch gets. Not something to route around.
    return {
      error: 'Could not fetch that image directly — often a cross-site block. ' +
        'Save it locally and use "Choose a file" instead.',
    };
  }
}
